<script setup>
import { ref, onMounted } from 'vue';

const currentLineIndex = ref(0);
const totalLines = 12;
const TYPING_INTERVAL = 300;  // Time between each line
const PAUSE_DELAY = 3000;     // Time to pause while text is visible
const FADE_DURATION = 500;    // Time for fade out animation

const mockAnalysisData = ref({
    recap: '<p>This research project focuses on understanding user needs for the onboarding experience and identifying pain points in the current flow.</p>',
    nextSteps: '<ul><li>Users need clearer value proposition before committing time to tutorials</li><li>Visual cues would help users understand progress through onboarding</li><li>Optional deep-dive content would better serve different user types</li></ul>',
    insights: '<p>Participants consistently mentioned confusion about feature benefits and expressed frustration with the length of the tutorial video.</p>'
});

onMounted(() => {
    // Start the typing animation immediately
    startTypingAnimation();
});

const resetAnimation = () => {
    setTimeout(() => {
        currentLineIndex.value = 0;
        setTimeout(() => {
            startTypingAnimation();
        }, FADE_DURATION);
    }, PAUSE_DELAY);
};

const startTypingAnimation = () => {
    currentLineIndex.value = 0;
    const interval = setInterval(() => {
        if (currentLineIndex.value < totalLines) {
            currentLineIndex.value++;
        } else {
            clearInterval(interval);
            resetAnimation();
        }
    }, TYPING_INTERVAL);
};
</script>

<template>
    <div class="max-w-full">
        <div class="flex items-center">
            <div class="w-full max-w-sm bg-white border border-gray-100 rounded-xl shadow-md pb-2">
                <!-- Header -->
                <div class="h-12 rounded-t-xl bg-green-500 text-white flex items-center justify-between px-4">
                    <div class="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 aspect-square" viewBox="0 0 512 512" fill="currentColor">
                            <path d="M493.7 .9L299.4 75.6l2.3-29.3c1-12.8-12.8-21.5-24-15.1L101.3 133.4C38.6 169.7 0 236.6 0 309C0 421.1 90.9 512 203 512c72.4 0 139.4-38.6 175.7-101.3L480.8 234.3c6.5-11.1-2.2-25-15.1-24l-29.3 2.3L511.1 18.3c.6-1.5 .9-3.2 .9-4.8C512 6 506 0 498.5 0c-1.7 0-3.3 .3-4.8 .9zM192 192a128 128 0 1 1 0 256 128 128 0 1 1 0-256zm0 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm16 96a16 16 0 1 0 0-32 16 16 0 1 0 0 32z"/>
                        </svg>
                        <span class="whitespace-nowrap font-bold">Catch up</span>
                    </div>
                </div>
                
                <!-- Content Section -->
                <div class="overflow-y-auto p-4 h-[350px]">
                    <div class="space-y-6">
                        <!-- Recap Section -->
                        <div class="type-in" :class="{ 'visible': currentLineIndex >= 1 }">
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 aspect-square text-green-500 mr-2" viewBox="0 0 384 512" fill="currentColor">
                                    <path d="M64 464c-8.8 0-16-7.2-16-16L48 64c0-8.8 7.2-16 16-16l160 0 0 80c0 17.7 14.3 32 32 32l80 0 0 288c0 8.8-7.2 16-16 16L64 464zM64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-293.5c0-17-6.7-33.3-18.7-45.3L274.7 18.7C262.7 6.7 246.5 0 229.5 0L64 0zm56 256c-13.3 0-24 10.7-24 24s10.7 24 24 24l144 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-144 0zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24l144 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-144 0z"/>
                                </svg>
                                <h3 class="text-md font-semibold text-gray-700">Project Recap</h3>
                            </div>
                            <div class="pl-6 text-gray-600 analysis-content text-left type-in" :class="{ 'visible': currentLineIndex >= 2 }" v-html="mockAnalysisData.recap"></div>
                        </div>
                        
                        <!-- Next Steps Section -->
                        <div class="type-in" :class="{ 'visible': currentLineIndex >= 4 }">
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 aspect-square text-green-500 mr-2" viewBox="0 0 512 512" fill="currentColor">
                                    <path d="M128 32l32 0c17.7 0 32 14.3 32 32l0 32L96 96l0-32c0-17.7 14.3-32 32-32zm64 96l0 320c0 17.7-14.3 32-32 32L32 480c-17.7 0-32-14.3-32-32l0-59.1c0-34.6 9.4-68.6 27.2-98.3C40.9 267.8 49.7 242.4 53 216L60.5 156c2-16 15.6-28 31.8-28l99.8 0zm227.8 0c16.1 0 29.8 12 31.8 28L459 216c3.3 26.4 12.1 51.8 25.8 74.6c17.8 29.7 27.2 63.7 27.2 98.3l0 59.1c0 17.7-14.3 32-32 32l-128 0c-17.7 0-32-14.3-32-32l0-320 99.8 0zM320 64c0-17.7 14.3-32 32-32l32 0c17.7 0 32 14.3 32 32l0 32-96 0 0-32zm-32 64l0 160-64 0 0-160 64 0z"/>
                                </svg>
                                <h3 class="text-md font-semibold text-gray-700">Next Steps</h3>
                            </div>
                            <div class="pl-6 text-gray-600 analysis-content text-left">
                                <div class="type-in" :class="{ 'visible': currentLineIndex >= 5 }" v-html="mockAnalysisData.nextSteps"></div>
                            </div>
                        </div>
                        
                        <!-- Insights Section -->
                        <div class="type-in" :class="{ 'visible': currentLineIndex >= 9 }">
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 aspect-square text-green-500 mr-2" viewBox="0 0 384 512" fill="currentColor">
                                    <path d="M272 384c9.6-31.9 29.5-59.1 49.2-86.2l0 0c5.2-7.1 10.4-14.2 15.4-21.4c19.8-28.5 31.4-63 31.4-100.3C368 78.8 289.2 0 192 0S16 78.8 16 176c0 37.3 11.6 71.9 31.4 100.3c5 7.2 10.2 14.3 15.4 21.4l0 0c19.8 27.1 39.7 54.4 49.2 86.2H272zM192 512c44.2 0 80-35.8 80-80V416H112v16c0 44.2 35.8 80 80 80zM112 176c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-61.9 50.1-112 112-112c8.8 0 16 7.2 16 16s-7.2 16-16 16c-44.2 0-80 35.8-80 80z"/>
                                </svg>
                                <h3 class="text-md font-semibold text-gray-700">Insights</h3>
                            </div>
                            <div class="pl-6 text-gray-600 analysis-content text-left type-in" :class="{ 'visible': currentLineIndex >= 10 }" v-html="mockAnalysisData.insights"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.type-in {
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease-out;
}

.type-in.visible {
    opacity: 1;
    transform: translateY(0);
}

.type-in:not(.visible) {
    transition-duration: 0.5s;
}

.analysis-content :deep(ul) {
    list-style-type: disc;
    margin-left: 1.5rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: left;
}

.analysis-content :deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: left;
}

.analysis-content :deep(p) {
    margin-bottom: 0.5rem;
    text-align: left;
}

.analysis-content :deep(h1), 
.analysis-content :deep(h2), 
.analysis-content :deep(h3) {
    font-weight: bold;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}
</style>
